import { withDialog } from "../compat/jquery-ui-dialog";
/**
 * SubmitFormData — the redirect submitter (REQ-CHK-5/6; research/05 §2.2,
 * §2.3, §2.5). Verbatim from v1 checkout_helper.js: soft token guard,
 * quantity element captured then REMOVED from the DOM (§8.6), FormData build,
 * in-form visible slider rewrite, new-flow properties[_cpc_properties]
 * append, outside-form collection (including the ineffective
 * `!== undefined` token check that throws when #cpcPriceToken is absent,
 * §8.7, and no :hidden skip), belt-and-suspenders token guard with the
 * bundled-$ #errDialog, then the jQuery POST to /cart/add (not .js) with
 * upload-progress text (first progress event suppressed, §8.15) and the
 * old-flow-only 422 retry (backoff, capped ≈16 s total, DIV-046); a new-flow
 * 422 undoes any partial add and stays on the page (DIV-044); a genuine
 * old-flow failure left after the retry (429, exhausted retries, 5xx,
 * network) now shows an error and stays on the page too (DIV-046, issue
 * #381) — an error callback firing on an actual 2xx still proceeds to
 * IsShopifyCartReady like before, and a new-flow non-422 failure still
 * proceeds to IsShopifyCartReady and redirects like success (§8.2, issue
 * #378).
 *
 * all jQuery is the BUNDLED copy (DIV-004).
 */
import $ from "jquery";
import { cpcAjax } from "../compat/jquery-ajax";
import { appState } from "../state/app-state";
import {
  GetVariantProperties,
  quantityPropertyNames,
  stripHiddenLookupFormulaAxes,
  collectExternalLineProperties,
  formatSliderValueForCart,
  isSliderInput,
} from "./variant-properties";
import { IsShopifyCartReady } from "./cart-ready";
import { handleStockLimitedAdd, prepareStockGuard, type StockGuard } from "./stock-limit";
import { ADD_TO_CART_FAILED_MESSAGE, showAddToCartError } from "./add-to-cart-error";
import { RestoreAllHiddenElements } from "./validate-inputs";
import { t, translateCartProperties, translateFormData } from "../i18n/translate";

// DIV-046 (issue #381): old-flow 422 retry backoff — 1, 2, 3, then 5 s steps,
// repeating the 5 s step until the cumulative wait is no longer under the
// budget below (≈16 s, close to the previous flat 15 × 1 s). A 429 never
// matches the `err.status === 422` guard below, so it (and any other status)
// skips retrying outright instead of hammering Shopify's rate limiter.
const OLD_FLOW_RETRY_DELAYS_MS = [1000, 2000, 3000, 5000];
const OLD_FLOW_RETRY_BUDGET_MS = 15000;

function nextOldFlowRetryDelayMs(retryCount: number): number {
  return OLD_FLOW_RETRY_DELAYS_MS[retryCount] ?? OLD_FLOW_RETRY_DELAYS_MS[OLD_FLOW_RETRY_DELAYS_MS.length - 1]!;
}

/**
 * Collects calculator data when the calculator element is outside the product form.
 * Handles input collection, variant ID, metadata, and quantity overrides.
 */
function collectCalculatorDataOutsideForm(
  calculatorElement: any,
  formElement: any,
  formData: FormData,
): any {
  console.log("AppBlock enabled for CPC");

  // Only collect calculator-specific inputs with 'calc-prop' class
  const calculatorInputs = calculatorElement.querySelectorAll(".calc-prop");
  let collectedCount = 0;
  let calculatorQuantity: any = null;

  calculatorInputs.forEach((input: any) => {
    // Handle cpc_isquantity elements - capture their value but don't post as properties.
    // Issue #111: only use the element if it is currently visible. When two
    // isQuantity elements exist (one per conditional branch), iterating without
    // a visibility check means the last one always wins regardless of which
    // branch the customer actually selected.
    if ($(input).data("cpc_isquantity") === true) {
      if (!$(input).is(":hidden")) {
        // BUG-107 / DIV-008: a Calculation ("Calculated figure") element flagged
        // "Use as Quantity" exposes its computed value as data-val on the visible
        // display node — its .value is undefined (the real value is on a sibling
        // type=hidden input that is not .calc-prop). Fall back to data-val only
        // when .value is empty, so the tile count reaches the cart line instead
        // of quantity defaulting to 1. NumberInputNew (no data-val) is unchanged.
        var qv: any = input.value;
        if ((qv == null || qv === "") && input.getAttribute("data-val") != null) {
          qv = input.getAttribute("data-val");
        }
        calculatorQuantity = qv;
        console.log(`Found visible cpc_isquantity element with value: ${calculatorQuantity}`);
      }
      return;
    }
    // Issue #126 / #110 (App Block path): lookup inputs live in the calculator
    // element (outside the product form). The FormData post-processing loop that
    // runs on formm below cannot reach them — they are in a different DOM subtree.
    // Handle them here. Two entries are written per axis:
    //   _-prefixed hidden entry — always present; Shopify hides it from customers.
    //   Visible label-keyed entry — only when data-lookup-show-in-order='true',
    //     stamped by lookup.ts based on whether the label starts with '_'.
    //     Labels starting with '_' are intentionally internal (issue #110 pattern);
    //     meaningful merchant labels like 'WIDTH, mm (3000 Max)' are shown (issue #126).
    if ($(input).data("lookup") !== undefined) {
      if ($(input).is(":hidden")) return;
      const calcname: string = $(input).data("calcname") || "";
      if (!calcname) return;
      var rawFloat = parseFloat(input.value);
      var maxDec = parseInt(input.getAttribute("data-maxdecimals") || "0", 10);
      if (isNaN(maxDec)) maxDec = 0;
      const lookupVal = !isNaN(rawFloat) ? rawFloat.toFixed(maxDec) : input.value;
      // Hidden _-prefixed entry — always, regardless of visibility intent.
      formData.set("properties[_" + calcname + "]", lookupVal);
      // Visible entry — only when the axis label is merchant-configured (not internal).
      const showInOrder = input.getAttribute("data-lookup-show-in-order") !== "false";
      if (showInOrder) {
        formData.set("properties[" + calcname + "]", lookupVal);
      }
      collectedCount++;
      return;
    }

    // Issue #Altanbutikken: skip hidden elements — conditional display hides them
    // via inline style.display="none"; they must NOT appear as cart line item
    // properties even when their value_when_not_displayed is non-zero. This
    // mirrors the isHidden guard in GetVariantProperties() and the :hidden guard
    // in SubmitViaThemeNativeSubmit's App Block injection loop.
    if ($(input).is(":hidden")) return;

    if (input.name && (input.value || input.type === "checkbox" || input.type === "radio")) {
      // Handle different input types like the GetVariantProperties function
      let value = input.value;
      let shouldAdd = true;

      if (input.type === "radio" && !input.checked) {
        shouldAdd = false;
      } else if (input.type === "checkbox") {
        value = input.checked ? "Checked" : "Unchecked";
      } else if (input.tagName === "SELECT") {
        const selectedOption = input.options[input.selectedIndex];
        if (selectedOption && selectedOption.dataset.label) {
          value = selectedOption.dataset.label;
        }
      } else if (isSliderInput(input)) {
        value = formatSliderValueForCart(input);
      } else if (input.type === "number") {
        // Issue #ScatterMats: round NumberInputNew values to maxdecimals
        var rawFloat = parseFloat(input.value);
        if (!isNaN(rawFloat)) {
          var maxDec = parseInt(input.getAttribute("data-maxdecimals") || "0", 10);
          if (isNaN(maxDec)) maxDec = 0;
          value = rawFloat.toFixed(maxDec);
        }
      }

      if (shouldAdd && value) {
        // Ensure proper properties format for Shopify
        let propertyName = input.name;
        if (propertyName.startsWith("cpc_upload_")) {
          let actualPropertyName = input.dataset.identifier;
          const fileInput = document.querySelector(
            `input[name='properties[${actualPropertyName}]']`,
          ) as any;
          if (fileInput !== null) {
            var fileUrlInput = fileInput.value;
            formData.append(`properties[${actualPropertyName}]`, fileUrlInput);
          }
        } else if (!propertyName.startsWith("properties[")) {
          propertyName = `properties[${input.dataset.calcname || input.name}]`;
          formData.append(propertyName, value);
        } else {
          formData.append(propertyName, value);
        }
        collectedCount++;
      }
    }
  });

  // Collect essential form fields when calculator is outside form
  // 1. Variant ID - crucial for add to cart
  const variantIdInput = calculatorElement.querySelector("#variantIdCustom");

  if (variantIdInput) {
    const variantId = variantIdInput.value;
    if (variantId && variantId !== "0") {
      formData.append("id", variantId);
      console.log(`Added variant ID: ${variantId}`);
    } else {
      console.warn("Variant ID is 0 or empty - this may cause add to cart issues");
    }
  } else {
    console.error("No variant ID found in calculator - add to cart will fail");
  }

  //Add _cpc_custom_cart_item token if available Needed for new checkout flow
  var cpcPriceToken = document.getElementById("cpcPriceToken") as any;
  if (cpcPriceToken !== undefined && cpcPriceToken.value !== null) {
    formData.append("properties[_cpc_custom_cart_item]", cpcPriceToken.value);
  }

  // Add calculator metadata
  //const calculatorId = calculatorElement.querySelector('#calculator')?.getAttribute('data-calcid') ||
   // calculatorElement.getAttribute('data-calcid') ||
   // calculatorElement.getAttribute('data-calculator-id') ||
   // calculatorElement.closest('[data-calculator-id]')?.getAttribute('data-calculator-id');
  //if (calculatorId) {
   // formData.append('properties[calculator_id]', calculatorId);
   // console.log(`Added calculator ID: ${calculatorId}`);
  //}

  console.log(`Captured ${collectedCount} calculator properties from outside form`);

  return calculatorQuantity;
}

export function SubmitFormData(calculatorElement: any = null): void {
  // Block submission if the price token hasn't resolved from the /variant API
  if (appState.cpc_isNewCheckoutFlowEnabled) {
    var tokenEl = document.getElementById("cpcPriceToken") as any;
    if (!tokenEl || !tokenEl.value || tokenEl.value === "none") {
      console.warn("Still preparing your price. Please wait a moment and try again.");
      $(".loading-anim-cpc").hide();
      return;
    }
  }

  $(".loading-anim-cpc").show();
  console.log("posting product data...");
  var formm: any = appState.productForm;

  let quantity: any = 1;
  // DIV-006 (BUG-081): capture the quantity WITHOUT removing the element —
  // removal meant a second add-to-cart on a live page silently posted
  // quantity 1. The field is excluded from the posted body by deleting its
  // FormData entry below (byte-equivalent to the old removal).
  const quantityFieldNames: string[] = [];
  console.log(formm);
  [...formm.elements].forEach((e) => {
    if ($(e).data("cpc_isquantity") === true) {
      // Issue #111: only use a quantity element that is currently visible.
      // When two isQuantity elements exist (each for a different conditional
      // branch), iterating without a visibility check means the last one always
      // wins — regardless of which branch the customer actually selected.
      // BUG-107 / DIV-008: a Calculation ("Calculated figure") element flagged
      // "Use as Quantity" carries the flag on a type=hidden input (value kept in
      // sync by EvaluateCalculationElements). jQuery :hidden ALWAYS matches a
      // type=hidden input, so its conditional-branch state is read off the
      // inline display of the sibling .element wrapper — the exact node, and the
      // exact check, ProcessConditionalElements uses. Without this a visible calc
      // quantity is dropped (→ defaults to 1) or a hidden-branch one wrongly wins.
      var el = e as HTMLInputElement;
      var branchHidden: boolean;
      if (el.type === "hidden") {
        var wrapper = el.previousElementSibling as HTMLElement | null;
        branchHidden = !!wrapper && wrapper.style.display === "none";
      } else {
        branchHidden = $(e).is(":hidden");
      }
      if (branchHidden) {
        // Still track the name so FormData.delete() removes it below.
        let hiddenName = $(e).attr("name");
        if (hiddenName) quantityFieldNames.push(hiddenName);
        return;
      }
      let name = $(e).attr("name");
      quantity = formm.elements[name!].value;
      if (name) quantityFieldNames.push(name);
    }
  });

  // var dataString = $(formm).serialize();
  var formData = new FormData(formm);
  // DIV-006: quantity element stays in the DOM (BUG-081) — drop its
  // serialized entry so the body matches the old remove-then-serialize shape,
  // and re-enable hidden-element inputs now that the snapshot is taken
  // (BUG-028; disabled inputs were excluded from the FormData above).
  quantityFieldNames.forEach((n) => formData.delete(n));
  // Issue #110 / #126 (in-form path): Lookup inputs rendered inside the product
  // form have name="properties[<label>]" (e.g. "properties[WIDTH, mm (3000 Max)]").
  // Delete the raw FormData entry; re-add as _-prefixed (always hidden); also add
  // the visible entry only when data-lookup-show-in-order is not 'false'.
  // This satisfies both #110 (generic '_Input 1' labels stay hidden) and #126
  // (meaningful 'WIDTH, mm' labels appear in Shopify order line item properties).
  // For App Block calculators (outside form) this loop is a no-op — the inputs
  // are in calculatorElement, not formm; that path is handled above.
  formm.querySelectorAll(".calc-prop[data-lookup='true']").forEach(function (input: any) {
    const rawName: string | undefined = input.name;
    if (!rawName || !rawName.startsWith("properties[")) return;
    if ($(input).is(":hidden")) return;
    const calcname: string = $(input).data("calcname") || "";
    if (!calcname) return;
    var rawFloat = parseFloat(input.value);
    var maxDec = parseInt(input.getAttribute("data-maxdecimals") || "0", 10);
    if (isNaN(maxDec)) maxDec = 0;
    const lookupVal = !isNaN(rawFloat) ? rawFloat.toFixed(maxDec) : input.value;
    // Always delete the raw entry and re-add _-prefixed (hidden from customers).
    formData.delete(rawName);
    formData.set(rawName.replace("properties[", "properties[_"), lookupVal);
    // Visible entry only when the axis label is merchant-configured (not internal).
    const showInOrder = input.getAttribute("data-lookup-show-in-order") !== "false";
    if (showInOrder) {
      formData.set("properties[" + calcname + "]", lookupVal);
    }
  });
  RestoreAllHiddenElements();
  formData.append("quantity", quantity);

  // In-form sliders serialize their raw numeric value via native FormData; rewrite
  // them to the unit-formatted display value so the cart line item matches the UI.
  // (App Block / outside-form sliders are handled in collectCalculatorDataOutsideForm.)
  formm.querySelectorAll(".cpc-slider").forEach(function (slider: any) {
    if (!slider.name || !slider.name.startsWith("properties[")) return;
    if ($(slider).is(":hidden")) return;
    formData.set(slider.name, formatSliderValueForCart(slider));
  });

  // Issue #ScatterMats: rewrite NumberInputNew float residue to maxdecimals.
  // native FormData serialises input.value verbatim — "0.7000000000000001" instead
  // of "0.7". Apply toFixed(maxdecimals) here, matching the slider rewrite above.
  //
  // DIV-016: this loop runs AFTER the isQuantity deletion above and matches
  // EVERY `.numInput-cpc` in the form, isQuantity included — a NumberInputNew
  // flagged "Use as Quantity" carries that class same as any other number
  // input. `formData.set()` on a key already removed re-adds it, so an
  // in-form isQuantity NumberInputNew silently regained its immutable
  // "properties[Quantity]" cart-line property here, undoing the deletion a
  // few lines up (and DIV-010) for the one path (redirect submitter,
  // in-form calculator) neither of those fixes touched. Recurrence #3 on the
  // same report (ACT Foam & Rubber, Crisp session_6a3a8fb2) after DIV-010
  // and DIV-013 both shipped — reproduced live on their store: the calculator
  // sits inside the product form and posts via `/cart/add` (redirect, not
  // the drawer/native-submit path DIV-013 fixed), so this loop was the only
  // remaining place still re-emitting the property. Skip isQuantity inputs
  // here exactly as the deletion above already does.
  formm.querySelectorAll(".numInput-cpc").forEach(function (input: any) {
    if (!input.name || !input.name.startsWith("properties[")) return;
    if ($(input).is(":hidden")) return;
    if ($(input).data("cpc_isquantity") === true) return;
    var rawFloat = parseFloat(input.value);
    if (isNaN(rawFloat)) return;
    var maxDec = parseInt(input.getAttribute("data-maxdecimals") || "0", 10);
    if (isNaN(maxDec)) maxDec = 0;
    formData.set(input.name, rawFloat.toFixed(maxDec));
  });

  if (appState.cpc_isNewCheckoutFlowEnabled === true) {
    let cpc_Properties = GetVariantProperties();
    // Strip Shopify-internal and upload metadata keys before stringifying.
    // The Cart Transform function already filters these out post-parse, but
    // including them in _cpc_properties inflates the per-line CT input payload.
    // At 15+ cart lines the combined payload can exceed Shopify's CT size limit,
    // causing silent fallback to the base variant price. Removing them here
    // keeps the blob small without losing any data the CT function actually needs.
    // DIV-010: the "Use as Quantity" element is the line quantity (posted below),
    // not a line attribute — see quantityPropertyNames().
    const quantityKeys = quantityPropertyNames();
    const cpcPropsFiltered = Object.fromEntries(
      Object.entries(Object.assign({}, cpc_Properties)).filter(
        ([key]) =>
          !key.startsWith('shopify_') &&
          !key.startsWith('cpc_upload_') &&
          quantityKeys.indexOf(key) === -1
      )
    );
    // Issue #83: a Lookup element's "isformula" axis is a permanently-hidden
    // formula-feed value (never meant to be shopper-visible), but GVP includes
    // it for /variant pricing — see stripHiddenLookupFormulaAxes().
    stripHiddenLookupFormulaAxes(cpcPropsFiltered);
    // Crisp session_42d55424 (LiquidDreamsDesign/Uploadly): the Cart Transform
    // function rebuilds the line's attributes from THIS blob alone, so a field
    // another app or the theme already wrote onto the form (not a .calc-prop)
    // must ride along inside it or it is silently dropped at checkout. Merge
    // UNDER the calculator's own properties so a same-named CPC field always wins.
    const mergedProps = Object.assign(
      {},
      collectExternalLineProperties(formm),
      cpcPropsFiltered,
    );
    // Translations v1: the blob's keys become the checkout/order line attributes
    // (the Cart Transform function builds them from these keys), so they are the
    // shopper's language. The /variant post above stays canonical — it is the
    // pricing channel.
    formData.append(
      "properties[_cpc_properties]",
      JSON.stringify(translateCartProperties(mergedProps)),
    );
  }

  // Handle calculator data collection when calculator is outside the form (App Block support)
  if (calculatorElement && !formm.contains(calculatorElement)) {
    const calculatorQuantity = collectCalculatorDataOutsideForm(calculatorElement, formm, formData);

    // Override quantity if calculator had cpc_isquantity element
    if (calculatorQuantity) {
      formData.set("quantity", calculatorQuantity);
      console.log(`Updated quantity from cpc_isquantity element: ${calculatorQuantity}`);
    }
  } else if (calculatorElement && formm.contains(calculatorElement)) {
    console.log("Calculator is inside form - FormData will capture inputs automatically");
  } else {
    console.log("No calculator element provided");
  }

  // Guard: if the Cart Transform token is still "none" (i.e. the /variant API
  // call hasn't resolved yet, or returned an invalid value), abort the submission.
  // Sending "none" to Shopify means the Cart Transform function finds no record
  // at checkout and silently falls back to the base variant price — this is the
  // root cause of intermittent wrong-price-at-checkout bugs (e.g. with large carts).
  //
  // Note: the primary guard lives in HandleAddToCartEvent (checkout/add-to-cart.ts)
  // which should prevent reaching here with an unresolved token. This is a
  // belt-and-suspenders check for any edge path that bypasses that guard.
  var cpcToken = formData.get("properties[_cpc_custom_cart_item]");
  if (appState.cpc_isNewCheckoutFlowEnabled && (cpcToken === "none" || !cpcToken)) {
    console.error(
      "CPC: SubmitFormData called with missing/invalid price token — aborting to prevent wrong price at checkout. Token:",
      cpcToken,
    );
    $(".loading-anim-cpc").hide();
    $("#errDialog").attr("title", "Error");
    $("#dialogMsg").text(t("Something went wrong calculating your price. Please try again."));
    withDialog(() => ($("#errDialog") as any).dialog({ modal: true }));
    // DIV-006: abort without navigation must not wedge the in-flight guard —
    // same stuck-flag class as BUG-020 (this guard postdates the FIX-PLAN).
    appState.cpc_isCheckoutInProgress = false;
    return;
  }

  // Translations v1: the theme's cart page renders THESE entries (channel 2), so
  // their keys and choice values are swapped last, after every other rewrite.
  translateFormData(formData);

  console.log("Final FormData to be sent:", formData);
  // DIV-044: a multi-unit new-flow add first notes the cart line's quantity,
  // so a stock-limited partial add can be undone (see stock-limit.ts).
  const pendingGuard = prepareStockGuard(formData);
  if (pendingGuard) pendingGuard.then((guard) => submitAddToCart(formData, calculatorElement, 0, guard));
  else submitAddToCart(formData, calculatorElement, 0);
}

export function submitAddToCart(
  formData: FormData,
  calculatorElement: any,
  retryCount: number,
  stockGuard: StockGuard | null = null,
  elapsedMs: number = 0,
): void {
  // DIV-043: cpcAjax, not $.ajax — immune to a theme app patching the shared
  // jQuery's .ajax (see compat/jquery-ajax.ts).
  cpcAjax({
    type: "POST",
    url: "/cart/add",
    data: formData,
    xhr: function () {
      var xhr = new (window as any).XMLHttpRequest();
      if (
        appState.calculatorStyling !== null &&
        appState.calculatorStyling.fileUploadProgressText !== null &&
        appState.calculatorStyling.fileUploadProgressText !== ""
      ) {
        let cpc_file_upload_count = 1;
        xhr.upload.addEventListener(
          "progress",
          function (evt: any) {
            cpc_file_upload_count++;
            if (evt.lengthComputable && cpc_file_upload_count > 2) {
              var percentComplete: any = (evt.loaded / evt.total) * 100;
              console.log("percentComplete", percentComplete);
              percentComplete = percentComplete.toFixed(2);
              $("#cpc_loader_text").html(
                appState.calculatorStyling.fileUploadProgressText +
                " " +
                percentComplete +
                " %",
              );
            }
          },
          false,
        );
      }
      return xhr;
    },
    //timeout: 1200000,
    processData: false,
    contentType: false,
    success: function () {
      if (appState.calculatorStyling !== null && appState.calculatorStyling.loaderText !== "")
        $("#cpc_loader_text").html(appState.calculatorStyling.loaderText);
      IsShopifyCartReady();
    },
    error: function (err: any) {
      console.log("add to cart request errored", err);

      if (
        !appState.cpc_isNewCheckoutFlowEnabled &&
        err.status === 422 &&
        elapsedMs < OLD_FLOW_RETRY_BUDGET_MS
      ) {
        const delay = nextOldFlowRetryDelayMs(retryCount);
        console.log(
          "CPC: add to cart failed, retrying in " + delay + " ms... attempt " + (retryCount + 1),
        );
        setTimeout(function () {
          submitAddToCart(formData, calculatorElement, retryCount + 1, null, elapsedMs + delay);
        }, delay);
        return;
      }

      // DIV-044 (issue #386): in the new flow a 422 means Shopify refused some or
      // all of the quantity (stock, or its add-to-cart limit). Redirecting would
      // check out the partial quantity, so undo it and stay on the page.
      // DIV-047 (issue #430): handleStockLimitedAdd now reads err's description
      // to tell a real stock rejection from a Draft/unavailable product or any
      // other 422, rather than reporting every one of them as a stock problem.
      if (appState.cpc_isNewCheckoutFlowEnabled && err.status === 422) {
        void handleStockLimitedAdd(stockGuard, err);
        return;
      }

      // DIV-046 (issue #381): the old flow no longer treats a genuine failure —
      // a 429 (Shopify's rate limit, tripped by the retry above on a
      // slow-to-propagate variant), an exhausted 422 retry budget, a network
      // failure (status 0), or a 5xx — as success. It shows the shopper an
      // error and stays on the page instead of calling IsShopifyCartReady()
      // and redirecting like the add worked. A jQuery error callback can also
      // fire on an actual 2xx (e.g. a response body it can't parse); Shopify
      // did add the item then, so that case still falls through to
      // IsShopifyCartReady() below, unchanged. The new flow's non-422 errors
      // are unchanged here too (issue #378).
      if (!appState.cpc_isNewCheckoutFlowEnabled && (err.status === 0 || err.status >= 400)) {
        console.log("CPC: add to cart failed, giving up (status " + err.status + ")");
        showAddToCartError(ADD_TO_CART_FAILED_MESSAGE);
        return;
      }

      if (appState.calculatorStyling !== null && appState.calculatorStyling.loaderText !== "")
        $("#cpc_loader_text").html(appState.calculatorStyling.loaderText);
      IsShopifyCartReady();
    },
  });
}
